import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { getKPIs, getAlerts, getTasks, getUsers, getSeguimientoStatus, getTask } from '../utils/api';
import TaskDrawer from '../components/TaskDrawer';
import QuickTaskPanel from '../components/QuickTaskPanel';
import { navigateWithFilters } from '../utils/navigation';
import { getCurrentWeekRange, getWeekLabel } from '../utils/dateHelpers';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import DashboardUsuario from './DashboardUsuario';
import GlobalSearch from '../components/GlobalSearch';

const CountUp = ({ value, duration = 800 }) => {
  const [count, setCount] = useState(0);
  useEffect(() => {
    let start = 0;
    const end = parseInt(value);
    if (start === end) return;
    const increment = (end - start) / (duration / 16);
    const timer = setInterval(() => {
      start += increment;
      if (start >= end) { setCount(end); clearInterval(timer); }
      else setCount(Math.floor(start));
    }, 16);
    return () => clearInterval(timer);
  }, [value, duration]);
  return <span>{count}</span>;
};

const ACCENT_COLORS = ['#6366F1','#10B981','#F59E0B','#8B5CF6','#EC4899','#06B6D4','#84CC16','#EF4444'];

const MONTHS = ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'];

const formatWeekRange = (weekStartIso) => {
  if (!weekStartIso) return '';
  const d = new Date(weekStartIso);
  const fri = new Date(d);
  fri.setDate(d.getDate() + 6);
  return `${d.getDate()} ${MONTHS[d.getMonth()]} — ${fri.getDate()} ${MONTHS[fri.getMonth()]}`;
};

const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [kpis, setKpis] = useState(null);
  const [alerts, setAlerts] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [chartsExpanded, setChartsExpanded] = useState(false);
  const [seguimientoItems, setSeguimientoItems] = useState([]);
  const [prevSeguimientoItems, setPrevSeguimientoItems] = useState([]);
  const [weekStart, setWeekStart] = useState('');
  const [drawerTask, setDrawerTask] = useState(null);
  const [quickPanel, setQuickPanel] = useState({ open: false, title: '', tasks: [], loading: false, accentColor: '#10B981' });

  const { weekStart: weekStartDate } = getCurrentWeekRange();
  const weekLabel = getWeekLabel(weekStartDate);

  const openTaskDrawer = async (taskId) => {
    try { const res = await getTask(taskId); setDrawerTask(res.data); } catch {}
  };

  const openQuickPanel = async (title, fetchFn, accentColor = '#10B981') => {
    setQuickPanel({ open: true, title, tasks: [], loading: true, accentColor });
    try {
      const res = await fetchFn();
      setQuickPanel(prev => ({ ...prev, tasks: res.data || [], loading: false }));
    } catch {
      setQuickPanel(prev => ({ ...prev, loading: false }));
    }
  };

  useEffect(() => {
    if (user?.role === 'Presidencia' || user?.role === 'Gerencia') loadData();
  }, [user?.role]);

  const getPrevWeekStart = () => {
    const today = new Date();
    const daysSinceSat = (today.getDay() + 1) % 7;
    const sat = new Date(today);
    sat.setDate(today.getDate() - daysSinceSat - 7);
    return sat.toISOString().split('T')[0];
  };

  const loadData = async () => {
    try {
      const prevWS = getPrevWeekStart();
      const [kpisRes, alertsRes, usersRes, segRes, prevSegRes] = await Promise.all([
        getKPIs(), getAlerts(), getUsers(),
        getSeguimientoStatus(),
        getSeguimientoStatus(prevWS)
      ]);
      setKpis(kpisRes.data);
      setAlerts(alertsRes.data);
      setUsers(usersRes.data);
      setSeguimientoItems(segRes.data.items || []);
      setWeekStart(segRes.data.week_start || '');
      setPrevSeguimientoItems(prevSegRes.data.items || []);
    } catch (e) {

    } finally {
      setLoading(false);
    }
  };

  if (user?.role === 'Usuario') return <DashboardUsuario user={user} />;

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
    </div>
  );

  // Group seguimiento items by responsible user
  const teamWeekData = (() => {
    const grouped = {};
    seguimientoItems.forEach(item => {
      const uid = item.task.responsible_user_id || '__unassigned__';
      if (!grouped[uid]) {
        grouped[uid] = {
          user_id: uid,
          name: item.responsible_name,
          department: '',
          deptCounts: {},
          tasks: [],
          updates_count: 0,
          total_count: 0,
          has_bloqueo: false,
          avg_pct: 0,
        };
      }
      grouped[uid].tasks.push(item);
      grouped[uid].total_count++;
      if (item.has_update) grouped[uid].updates_count++;
      if (item.update?.bloqueo) grouped[uid].has_bloqueo = true;
      // Track most frequent real department name
      const dept = item.department_name;
      if (dept && dept !== 'Sin Departamento') {
        grouped[uid].deptCounts[dept] = (grouped[uid].deptCounts[dept] || 0) + 1;
      }
    });

    Object.values(grouped).forEach(person => {
      const updatedTasks = person.tasks.filter(t => t.has_update);
      person.avg_pct = updatedTasks.length > 0
        ? Math.round(updatedTasks.reduce((sum, t) => sum + (t.update?.cumplimiento_pct || 0), 0) / updatedTasks.length)
        : 0;
      // Use most frequent department, or empty string (never show "Sin Departamento")
      const depts = Object.entries(person.deptCounts).sort((a, b) => b[1] - a[1]);
      person.department = depts.length > 0 ? depts[0][0] : '';
      delete person.deptCounts;
    });

    return Object.values(grouped)
      .sort((a, b) => {
        if (a.has_bloqueo !== b.has_bloqueo) return a.has_bloqueo ? -1 : 1;
        const aRatio = a.total_count > 0 ? a.updates_count / a.total_count : 0;
        const bRatio = b.total_count > 0 ? b.updates_count / b.total_count : 0;
        if (aRatio !== bRatio) return aRatio - bRatio;
        return a.name.localeCompare(b.name);
      })
      .map((person, idx) => ({ ...person, color: ACCENT_COLORS[idx % ACCENT_COLORS.length] }));
  })();

  const totalUpdates = teamWeekData.reduce((sum, p) => sum + p.updates_count, 0);
  const totalTasks = teamWeekData.reduce((sum, p) => sum + p.total_count, 0);
  const weekPct = totalTasks > 0 ? Math.round((totalUpdates / totalTasks) * 100) : 0;

  // Build prev-week lookup by person
  const prevByPerson = (() => {
    const map = {};
    prevSeguimientoItems.forEach(item => {
      const uid = item.task.responsible_user_id || '__unassigned__';
      if (!map[uid]) map[uid] = { pcts: [], updates_count: 0, total_count: 0 };
      map[uid].total_count++;
      if (item.has_update) {
        map[uid].updates_count++;
        map[uid].pcts.push(item.update?.cumplimiento_pct || 0);
      }
    });
    Object.values(map).forEach(p => {
      p.avg_pct = p.pcts.length > 0
        ? Math.round(p.pcts.reduce((a, b) => a + b, 0) / p.pcts.length)
        : 0;
    });
    return map;
  })();

  const prevTotalUpdates = prevSeguimientoItems.filter(i => i.has_update).length;
  const prevTotalTasks = prevSeguimientoItems.length;
  const prevWeekPct = prevTotalTasks > 0 ? Math.round((prevTotalUpdates / prevTotalTasks) * 100) : null;
  const weekTrend = prevWeekPct !== null ? weekPct - prevWeekPct : null;

  const weekDisplayLabel = formatWeekRange(weekStart) || weekLabel;

  return (
    <>
    <div className="min-h-screen">

      {/* Topbar */}
      <div className="px-6 py-4 border-b border-border bg-background sticky top-0 z-10">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h1 className="text-lg font-bold text-foreground">Dashboard</h1>
            <p className="text-xs text-muted-foreground">{weekDisplayLabel}</p>
          </div>
          <div className="flex items-center gap-2">
            <GlobalSearch />
            {(user?.role === 'Presidencia' || user?.role === 'Gerencia') && (
              <motion.button
                whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }}
                onClick={() => navigate('/tasks/create')}
                className="btn-primary flex items-center gap-1.5 text-sm"
              >
                <span className="text-base leading-none">+</span> Nueva Tarea
              </motion.button>
            )}
            {user?.role === 'Presidencia' && (
              <motion.button
                whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }}
                onClick={() => navigate('/reports/weekly')}
                className="btn-secondary text-sm"
              >
                Cierre Semanal
              </motion.button>
            )}
          </div>
        </div>
      </div>

      <div className="p-6 space-y-6 max-w-[1400px] mx-auto">

        {/* KPI Strip */}
        {kpis && (
          <div className="decko-card overflow-hidden">
            <div className="flex divide-x divide-border">
              {[
                { label: 'Vencidas', value: kpis?.kpis?.tareas_vencidas || 0, color: '#EF4444', hoverBg: 'hover:bg-red-500/5', fetch: () => getTasks({ overdue: 'true' }) },
                { label: 'Próximas 48h', value: alerts?.due_soon_48h?.count || 0, color: '#F59E0B', hoverBg: 'hover:bg-yellow-500/5', fetch: () => getTasks({ due_soon: 'true' }) },
                { label: 'En Espera', value: kpis?.tasks_by_status?.['En Espera'] || 0, color: '#F97316', hoverBg: 'hover:bg-orange-500/5', fetch: () => getTasks({ status: 'En Espera' }) },
                { label: 'En Progreso', value: kpis?.tasks_by_status?.['En Progreso'] || 0, color: '#3B82F6', hoverBg: 'hover:bg-blue-500/5', fetch: () => getTasks({ status: 'En Progreso' }) },
              ].map(kpi => (
                <button
                  key={kpi.label}
                  className={`flex-1 px-4 py-4 flex flex-col items-center gap-1 transition-colors ${kpi.hoverBg} group`}
                  onClick={() => openQuickPanel(kpi.label, kpi.fetch, kpi.color)}
                >
                  <span className="text-2xl font-black" style={{ color: kpi.color }}>
                    <CountUp value={kpi.value} />
                  </span>
                  <span className="text-[11px] text-muted-foreground uppercase tracking-wide font-semibold group-hover:text-foreground transition-colors">{kpi.label}</span>
                </button>
              ))}
              <div className="flex-1 px-4 py-4 flex flex-col items-center gap-1">
                <span className="text-2xl font-black text-foreground">
                  <CountUp value={kpis?.kpis?.total_tasks || 0} />
                </span>
                <span className="text-[11px] text-muted-foreground uppercase tracking-wide font-semibold">Total Activas</span>
              </div>
            </div>
          </div>
        )}

        {/* EQUIPO — protagonista */}
        {teamWeekData.length > 0 && (
          <section>
            {/* Section header */}
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-sm font-bold text-foreground uppercase tracking-wide">Equipo — Esta Semana</h2>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {totalUpdates} de {totalTasks} actualizaciones recibidas
                </p>
              </div>
              <div className="flex items-center gap-3">
                {weekTrend !== null && (
                  <span className={`text-xs font-bold tabular-nums ${weekTrend > 0 ? 'text-success' : weekTrend < 0 ? 'text-destructive' : 'text-muted-foreground'}`}>
                    {weekTrend > 0 ? '↑' : weekTrend < 0 ? '↓' : '→'}{Math.abs(weekTrend)}% vs semana ant.
                  </span>
                )}
                <div className="w-28 h-1.5 bg-secondary rounded-full overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${weekPct}%` }}
                    transition={{ duration: 0.8 }}
                    className={`h-full rounded-full ${weekPct >= 80 ? 'bg-success' : weekPct >= 50 ? 'bg-warning' : 'bg-destructive'}`}
                  />
                </div>
                <span className={`text-sm font-black tabular-nums ${weekPct >= 80 ? 'text-success' : weekPct >= 50 ? 'text-warning' : 'text-destructive'}`}>
                  {weekPct}%
                </span>
              </div>
            </div>

            {/* Person rows — status only, detail en Mi Semana */}
            <div className="decko-card overflow-hidden divide-y divide-border">
              {teamWeekData.map((person) => {
                const allDone = person.updates_count === person.total_count;
                const noneDone = person.updates_count === 0;
                const prev = prevByPerson[person.user_id];
                const pctDelta = prev && person.avg_pct > 0 ? person.avg_pct - prev.avg_pct : null;

                return (
                  <div
                    key={person.user_id}
                    className="px-5 py-3 flex items-center gap-4 hover:bg-white/[0.02] transition-colors cursor-pointer"
                    onClick={() => navigate('/mi-semana')}
                  >
                    <div
                      className="w-8 h-8 rounded-lg flex items-center justify-center text-white font-black text-sm flex-shrink-0"
                      style={{ background: person.color }}
                    >
                      {person.name.charAt(0).toUpperCase()}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-foreground text-sm">{person.name}</span>
                        {person.has_bloqueo && (
                          <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-destructive/10 text-destructive border border-destructive/20 whitespace-nowrap">
                            Bloqueado
                          </span>
                        )}
                      </div>
                      {person.department && (
                        <p className="text-xs text-muted-foreground truncate">{person.department}</p>
                      )}
                    </div>

                    <div className="flex items-center gap-3 flex-shrink-0">
                      {person.updates_count > 0 && (
                        <div className="hidden sm:flex items-center gap-2">
                          <div className="w-16 h-1 bg-secondary rounded-full overflow-hidden">
                            <div className="h-full rounded-full" style={{ width: `${person.avg_pct}%`, background: person.color }} />
                          </div>
                          <span className="text-xs font-bold tabular-nums" style={{ color: person.color }}>{person.avg_pct}%</span>
                          {pctDelta !== null && (
                            <span className={`text-[10px] font-bold tabular-nums ${pctDelta > 0 ? 'text-success' : pctDelta < 0 ? 'text-destructive' : 'text-muted-foreground'}`}>
                              {pctDelta > 0 ? '↑' : pctDelta < 0 ? '↓' : '→'}{Math.abs(pctDelta)}
                            </span>
                          )}
                        </div>
                      )}

                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full tabular-nums ${
                        allDone ? 'bg-success/10 text-success' :
                        noneDone ? 'bg-secondary text-muted-foreground' :
                        'bg-warning/10 text-warning'
                      }`}>
                        {person.updates_count}/{person.total_count}
                      </span>

                      <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
                        allDone ? 'bg-success' :
                        noneDone ? 'bg-muted-foreground/25' :
                        'bg-warning animate-pulse'
                      }`} />
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* Analytics — colapsado */}
        <section>
          <button
            onClick={() => setChartsExpanded(!chartsExpanded)}
            className="flex items-center gap-2 text-xs font-semibold text-muted-foreground uppercase tracking-wide hover:text-foreground transition-colors w-full"
          >
            <span>Analytics</span>
            <svg className={`w-4 h-4 transition-transform ${chartsExpanded ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {chartsExpanded && kpis && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4"
            >
              <div className="decko-card p-5">
                <h3 className="text-sm font-semibold text-foreground mb-4">Por Estado</h3>
                <ResponsiveContainer width="100%" height={240}>
                  <PieChart>
                    <Pie
                      data={Object.entries(kpis.tasks_by_status || {}).filter(([_, v]) => v > 0).map(([name, value]) => ({ name, value }))}
                      dataKey="value" cx="50%" cy="50%" outerRadius={80} label
                      onClick={data => navigateWithFilters(navigate, { status: data.name })}
                    >
                      {Object.keys(kpis.tasks_by_status || {}).map((_, i) => (
                        <Cell key={i} fill={['#10B981','#06B6D4','#8B5CF6','#F59E0B','#EF4444'][i % 5]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="decko-card p-5">
                <h3 className="text-sm font-semibold text-foreground mb-4">Por Prioridad</h3>
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={Object.entries(kpis.tasks_by_priority || {}).map(([name, value]) => ({ name, value }))}>
                    <XAxis dataKey="name" tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }} />
                    <YAxis tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }} />
                    <Tooltip />
                    <Bar dataKey="value" fill="#10B981" radius={[6, 6, 0, 0]}
                      onClick={data => navigateWithFilters(navigate, { priority: data.name })} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </motion.div>
          )}
        </section>

      </div>
    </div>

    <QuickTaskPanel
      panel={quickPanel}
      onClose={() => setQuickPanel(prev => ({ ...prev, open: false }))}
      onOpenTask={id => { setQuickPanel(prev => ({ ...prev, open: false })); openTaskDrawer(id); }}
      users={users}
    />

    {drawerTask && (
      <TaskDrawer
        task={drawerTask}
        users={users}
        onClose={() => setDrawerTask(null)}
        onUpdate={() => { setDrawerTask(null); loadData(); }}
      />
    )}
    </>
  );
};

export default Dashboard;
